import {
  type Event as RouterEvent,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
} from '@angular/router';

/** The boot shell itself: a sibling of `<app-root>`, so rendering the root does not clear it. */
export const BOOT_SHELL_SELECTOR = '#app-boot';

/**
 * The boot shell in `index.html`, and the one line of text inside it.
 *
 * Exported so the spec queries the same selector this code does, and read
 * from this file by `scripts/verify-boot-shell-tokens.mjs`, which holds
 * `index.html` to it: exactly one matching element, carrying the live-region
 * attributes the failure message is announced through.
 */
export const BOOT_LABEL_SELECTOR = '#app-boot .app-boot-label';

/** What the shell says once there is no longer an app on the way. */
export const BOOT_FAILURE_MESSAGE =
  'Commercial Margin Intelligence could not start. Reload the page to try again.';

/**
 * Tell the boot shell that the app is not coming.
 *
 * The shell is a sibling of `<app-root>`, which `app.ts` removes after the
 * first page renders. This is for the paths where nothing will render: a
 * bootstrap that rejects, or a first navigation that fails or is refused.
 * Without it a `position: fixed; inset: 0` overlay is left saying "Loading…"
 * over an app that will never arrive — a claim that cannot be dismissed and
 * never stops being false.
 *
 * The shell is deliberately left on screen. Removing it would hand the reader
 * the blank white page the shell exists to replace; the fix is for it to stop
 * saying the wrong thing, not to stop saying anything. The text lands in the
 * element that already carries `role="status" aria-live="polite"`, so the
 * change is announced without a second live region.
 */
export function reportBootFailure(root: ParentNode = document): void {
  const label = root.querySelector(BOOT_LABEL_SELECTOR);
  // No shell to correct — a host page that dropped it, or a test fixture.
  // Nothing to do, and nothing worth throwing over during a failed boot.
  if (!label) return;
  label.textContent = BOOT_FAILURE_MESSAGE;
}

/** How the first navigation settles for the shell: a page rendered, or none will. */
export type FirstNavigationOutcome = 'rendered' | 'failed';

/** Redirects and superseded navigations are still on their way to a page. */
const isStillNavigating = (code: NavigationCancellationCode | undefined): boolean =>
  code === NavigationCancellationCode.Redirect ||
  code === NavigationCancellationCode.SupersededByNewNavigation;

export function firstNavigationOutcome(event: RouterEvent): FirstNavigationOutcome | null {
  if (event instanceof NavigationEnd) return 'rendered';
  if (event instanceof NavigationError) return 'failed';
  if (event instanceof NavigationCancel) return isStillNavigating(event.code) ? null : 'failed';
  return null;
}

/** Remove the shell once a page has rendered under it. */
export function dismissBootShell(root: ParentNode = document): void {
  root.querySelector(BOOT_SHELL_SELECTOR)?.remove();
}
